import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import DOMPurify from 'dompurify';
import { Col, Row } from 'react-bootstrap';
import MyExamSlider from '../../exams/MyExamSlider';
import cameraIcon from '../../assets/images/bagIcon.png';
import profileLogo from '../../assets/images/c1.png';
import { AnswerIcon, DoubtShareIcon, DoubtUpvoteIcon } from '../../components/SvgIcons';
import AddDoubtModal from './modals/AddDoubt';
import BlockDoubtModal from './modals/BlockDoubt';
import ReportModal from './modals/DoubtReport';
import FlagImg from '../../assets/images/DoubtFlag.png';
import BlockUser from '../../assets/images/DoubtUser.png';
import { getStoredUser } from '../../utils/authStorage';
import { DOUBT_ROUTES } from './doubtRoutes';
import './DoubtTabs.css';
import './DoubtDetail.css';

const sliderSettings = { dots: false, infinite: false, speed: 500, slidesToShow: 3.1, slidesToScroll: 1, arrows: true, responsive: [{ breakpoint: 767, settings: { slidesToShow: 1.1 } }] };
const safelyRender = (value) => ({ __html: DOMPurify.sanitize(String(value || '')) });

const DoubtCard = ({ item, onOpen, onShare, onUpvote, onBookmark, onAddDoubt, onBlockDoubt, onReportDoubt, onEditDoubt, isUpvotePending, isBookmarkPending, userId }) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const selectedImage = selectedImageIndex == null ? '' : item.images[selectedImageIndex];
  const closeImageViewer = () => setSelectedImageIndex(null);
  const moveImage = (direction) => setSelectedImageIndex((current) => (current + direction + item.images.length) % item.images.length);
  
  const isOwner = Boolean(item.canEdit || (userId && userId === item.ownerUserId));

  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (selectedImageIndex == null) return;
      if (event.key === 'Escape') closeImageViewer();
      if (event.key === 'ArrowLeft') setSelectedImageIndex((current) => (current - 1 + item.images.length) % item.images.length);
      if (event.key === 'ArrowRight') setSelectedImageIndex((current) => (current + 1) % item.images.length);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex, item.images.length]);

  const imageSlides = item.images.map((image) => ({ image }));
  return <>
    <article className="question-card" onClick={() => onOpen?.(item)}>
      <div className="question-card__header">
        <div className="question-card__user"><div className="question-card__avatar"><img src={item.authorImage || profileLogo} alt="" /></div><div className="question-card__user-info"><div className="question-card__user-name">{item.authorName}</div><div className="question-card__time">{item.createdAt}</div></div></div>
        <div className="question-card__header-actions" style={{ position: 'relative' }}>
          <button type="button" className={item.bookmarkStatus ? 'is-bookmarked' : ''} aria-label={item.bookmarkStatus ? 'Bookmarked' : 'Bookmark'} disabled={isBookmarkPending} onClick={(event) => { event.stopPropagation(); onBookmark?.(item); }}><i className={`bi ${item.bookmarkStatus ? 'bi-bookmark-fill' : 'bi-bookmark'}`} /></button>
          
          <button type="button" aria-label="More options" onClick={(event) => { event.stopPropagation(); setIsDropdownOpen(prev => !prev); }}>
            <i className="bi bi-three-dots-vertical" />
          </button>
          {isDropdownOpen && (
            <div className="doubt-detail-menu" ref={dropdownRef}>
              {!isOwner ? (
                <>
                  <button type="button" onClick={(event) => { event.stopPropagation(); setIsDropdownOpen(false); onReportDoubt?.(item); }}>
                    Report
                  </button>
                  <button type="button" onClick={(event) => { event.stopPropagation(); setIsDropdownOpen(false); onBlockDoubt?.(item); }}>
                    Block
                  </button>
                  <button type="button" onClick={(event) => { event.stopPropagation(); setIsDropdownOpen(false); onAddDoubt?.(item); }}>
                    {item.isAddedToMyDoubts ? "Remove from Your Doubts" : "Add to Your Doubts"}
                  </button>
                </>
              ) : (
                <>
                  <button type="button" onClick={(event) => { event.stopPropagation(); setIsDropdownOpen(false); onEditDoubt?.(item); }}>
                    Edit
                  </button>
                  <button type="button" onClick={(event) => { event.stopPropagation(); setIsDropdownOpen(false); Swal.fire({ icon: 'info', title: 'Unavailable', text: 'Doubt deletion is currently unavailable.' }); }}>
                    Delete
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {item.subjectName ? <div className="question-card__module">{item.subjectName}</div> : null}
      {item.topicNames?.length ? <div className="doubt-card__topics">{item.topicNames.join(' • ')}</div> : null}
      <button type="button" className="doubt-card__open" onClick={() => onOpen?.(item)}><div className="question-card__title" dangerouslySetInnerHTML={safelyRender(item.title)} />{item.sourceModule ? <div className="question-card__source">Source Module: <span>{item.sourceModule}</span></div> : null}</button>
      {item.description ? <div className="question-card__detail"><span className="question-card__detail-label">Question:</span><span className={`question-card__detail-text${expanded ? ' expanded' : ''}`} dangerouslySetInnerHTML={safelyRender(item.description)} /><button type="button" className="question-card__see-more" onClick={(event) => { event.stopPropagation(); setExpanded((value) => !value); }}>{expanded ? 'See less' : 'See more'}</button></div> : null}
      {imageSlides.length ? <Row className={`dashboard-states mt-0${imageSlides.length === 1 ? ' single-image-slider' : ''}`} onClick={(event) => event.stopPropagation()}><Col md={12} xl={12}><MyExamSlider items={imageSlides} settings={sliderSettings} renderItem={(slideItem, index) => <div className="doubt-slider-item"><img src={slideItem.image} alt="Doubt attachment" className="doubt-slider-image" onClick={() => setSelectedImageIndex(index)} /></div>} /></Col></Row> : null}
      <div className="question-card__answer" onClick={(event) => { event.stopPropagation(); onOpen?.(item); }} style={{ cursor: 'pointer' }}><input type="text" placeholder="Answer Doubt" className="question-card__answer-input" readOnly style={{ cursor: 'pointer' }} /><button type="button" className="question-card__camera" aria-label="Attach image"><img src={cameraIcon} alt="" /></button></div>
      <div className="question-card__footer" onClick={(event) => event.stopPropagation()}><button type="button" className={item.isUpvoted ? 'is-upvoted' : ''} disabled={isUpvotePending} onClick={() => onUpvote?.(item)}><i className={`bi ${item.isUpvoted ? 'bi-hand-thumbs-up-fill' : 'bi-hand-thumbs-up'}`} /><span>{item.upvoteCount} Upvote</span></button><button type="button" onClick={() => onOpen?.(item)}><AnswerIcon /><span>{item.answerCount} Answer{item.answerCount === 1 ? '' : 's'}</span></button><button type="button" onClick={() => onShare?.(item)}><DoubtShareIcon /><span>Share</span></button></div>
    </article>
    {selectedImage ? <div className="image-viewer-overlay" onClick={closeImageViewer}><div className="image-viewer-header" onClick={(event) => event.stopPropagation()}><button type="button" className="image-viewer-back" onClick={closeImageViewer} aria-label="Back"><i className="bi bi-arrow-left" /></button><div className="image-viewer-counter">{selectedImageIndex + 1} / {item.images.length}</div><button type="button" className="image-viewer-close" onClick={closeImageViewer} aria-label="Close"><i className="bi bi-x-circle-fill" /></button></div>{item.images.length > 1 ? <button type="button" className="image-viewer-prev" onClick={(event) => { event.stopPropagation(); moveImage(-1); }} aria-label="Previous image"><i className="bi bi-chevron-left" /></button> : null}<div className="image-viewer-content" onClick={(event) => event.stopPropagation()}><img src={selectedImage} alt="Doubt attachment preview" className="image-viewer-image" /></div>{item.images.length > 1 ? <button type="button" className="image-viewer-next" onClick={(event) => { event.stopPropagation(); moveImage(1); }} aria-label="Next image"><i className="bi bi-chevron-right" /></button> : null}</div> : null}
  </>;
};

const DoubtShimmer = () => <div className="all-doubts__grid" aria-label="Loading doubts">{Array.from({ length: 4 }, (_, index) => <div className="question-card doubt-card-shimmer" key={index}><span /><span /><span /><span /></div>)}</div>;

const AllDoubts = ({ doubts = [], isLoading = false, isFetchingNextPage = false, hasNextPage = false, loadMoreRef, onOpen, onShare, onUpvote, onBookmark, isUpvotePending, isBookmarkPending, emptyMessage = 'No doubts found.', language = 'EN' }) => {
  const navigate = useNavigate();
  const [addDoubtModalOpen, setAddDoubtModalOpen] = useState(false);
  const [isBlockDoubtOpen, setIsBlockDoubtOpen] = useState(false);
  const [isDoubtReportOpen, setIsDoubtReportOpen] = useState(false);
  const [selectedDoubt, setSelectedDoubt] = useState(null);
  
  const storedUser = getStoredUser();
  const currentUserId = storedUser?.user_id ? Number(storedUser.user_id) : 0;

  const handleEditDoubt = (item) => {
    navigate(DOUBT_ROUTES.ask, {
      state: {
        selectedDoubt: item.raw,
        qbankBackTarget: { pathname: DOUBT_ROUTES.list, state: {} },
      },
    });
  };

  if (isLoading) return <DoubtShimmer />;
  if (!doubts.length) return <div className="doubts-empty-state">{emptyMessage}</div>;
  
  return (
    <>
      <div className="all-doubts">
        <div className="all-doubts__grid">
          {doubts.map((item) => (
            <DoubtCard 
              key={item.id} 
              item={item} 
              onOpen={onOpen} 
              onShare={onShare} 
              onUpvote={onUpvote} 
              onBookmark={onBookmark} 
              isUpvotePending={isUpvotePending} 
              isBookmarkPending={isBookmarkPending}
              userId={currentUserId}
              onAddDoubt={(item) => { setSelectedDoubt(item); setAddDoubtModalOpen(true); }}
              onBlockDoubt={(item) => { setSelectedDoubt(item); setIsBlockDoubtOpen(true); }}
              onReportDoubt={(item) => { setSelectedDoubt(item); setIsDoubtReportOpen(true); }}
              onEditDoubt={handleEditDoubt}
            />
          ))}
        </div>
        {hasNextPage ? <div ref={loadMoreRef} className="doubts-load-more">{isFetchingNextPage ? 'Loading more doubts...' : 'Scroll to load more'}</div> : null}
      </div>

      <AddDoubtModal
        open={addDoubtModalOpen}
        onClose={() => { setAddDoubtModalOpen(false); setSelectedDoubt(null); }}
        doubtId={selectedDoubt?.id}
        language={language}
        isAdded={selectedDoubt?.isAddedToMyDoubts}
      />
      <BlockDoubtModal
        open={isBlockDoubtOpen}
        onClose={() => { setIsBlockDoubtOpen(false); setSelectedDoubt(null); }}
        authorName={selectedDoubt?.authorName}
        doubtId={selectedDoubt?.id}
        language={language}
      />
      <ReportModal
        open={isDoubtReportOpen}
        onClose={() => { setIsDoubtReportOpen(false); setSelectedDoubt(null); }}
        doubtId={selectedDoubt?.id}
        language={language}
      />
    </>
  );
};

export default AllDoubts;
